import React, { useState } from "react";
import { Modal, Button, Form, Alert, Spinner } from "react-bootstrap";
import api from "../services/api";
import { useLanguage } from "../i18n/LanguageContext";

interface TwoFASetupModalProps {
  show: boolean;
  onHide: () => void;
  onSuccess: () => void;
}

const TwoFASetupModal: React.FC<TwoFASetupModalProps> = ({
  show,
  onHide,
  onSuccess,
}) => {
  const { t, dir } = useLanguage();
  const [step, setStep] = useState<"intro" | "qr" | "verify" | "backup">(
    "intro",
  );
  const [qrCode, setQrCode] = useState("");
  const [secret, setSecret] = useState("");
  const [verifyCode, setVerifyCode] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const handleSetup = async () => {
    setLoading(true);
    setError("");
    try {
      const response = await api.post("/2fa/setup");
      setQrCode(response.data.qrCode);
      setSecret(response.data.secret);
      setStep("qr");
    } catch (err: any) {
      setError(err.response?.data?.error || "Failed to setup 2FA");
    } finally {
      setLoading(false);
    }
  };

  const handleVerify = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError("");
    try {
      await api.post("/2fa/verify-setup", { token: verifyCode });
      setStep("backup");
    } catch (err: any) {
      setError(err.response?.data?.error || t("invalidCode"));
    } finally {
      setLoading(false);
    }
  };

  const handleFinish = () => {
    onSuccess();
    handleClose();
  };

  const handleClose = () => {
    setStep("intro");
    setQrCode("");
    setSecret("");
    setVerifyCode("");
    setError("");
    onHide();
  };

  return (
    <Modal show={show} onHide={handleClose} size="lg" centered dir={dir}>
      <Modal.Header closeButton>
        <Modal.Title>🔐 {t("setupTwoFA")}</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        {error && (
          <Alert variant="danger" dismissible onClose={() => setError("")}>
            {error}
          </Alert>
        )}

        {/* Step 1: Intro */}
        {step === "intro" && (
          <div className="text-center py-4">
            <div style={{ fontSize: "64px" }}>🔐</div>
            <h4 className="mt-3">{t("setupTwoFA")}</h4>
            <p className="text-muted">{t("twoFactorAuth")}</p>
            <Button
              variant="primary"
              size="lg"
              onClick={handleSetup}
              disabled={loading}
              className="mt-3"
            >
              {loading ? (
                <Spinner animation="border" size="sm" />
              ) : (
                t("next")
              )}
            </Button>
          </div>
        )}

        {/* Step 2: QR Code */}
        {step === "qr" && (
          <div>
            <h5>{t("scanQRCode")}</h5>
            <div className="text-center my-4">
              <img src={qrCode} alt="QR Code" style={{ maxWidth: "250px" }} />
            </div>
            <Alert variant="info">
              <strong>Can't scan?</strong> Enter this key manually in your app:
              <div className="mt-2">
                <code className="bg-light p-2 d-block">{secret}</code>
              </div>
            </Alert>
            <div className="d-flex justify-content-end gap-2">
              <Button variant="secondary" onClick={handleClose}>
                {t("cancel")}
              </Button>
              <Button variant="primary" onClick={() => setStep("verify")}>
                {t("next")}
              </Button>
            </div>
          </div>
        )}

        {/* Step 3: Verify */}
        {step === "verify" && (
          <div>
            <h5>{t("twoFactorAuth")}</h5>
            <p className="text-muted">{t("enterCode")}</p>
            <Form onSubmit={handleVerify}>
              <Form.Group className="my-4">
                <Form.Control
                  type="text"
                  placeholder="000000"
                  value={verifyCode}
                  onChange={(e) =>
                    setVerifyCode(e.target.value.replace(/\D/g, "").slice(0, 6))
                  }
                  maxLength={6}
                  style={{
                    fontSize: "24px",
                    textAlign: "center",
                    letterSpacing: "0.5em",
                    fontWeight: "bold",
                  }}
                  autoFocus
                />
              </Form.Group>
              <div className="d-flex justify-content-end gap-2">
                <Button variant="secondary" onClick={() => setStep("qr")}>
                  {t("back")}
                </Button>
                <Button
                  variant="primary"
                  type="submit"
                  disabled={verifyCode.length !== 6 || loading}
                >
                  {loading ? (
                    <Spinner animation="border" size="sm" />
                  ) : (
                    t("submit")
                  )}
                </Button>
              </div>
            </Form>
          </div>
        )}

        {/* Step 4: Success */}
        {step === "backup" && (
          <div>
            <Alert variant="success">
              <strong>✓ {t("twoFAEnabledSuccess")}</strong>
            </Alert>
            <div className="text-end">
              <Button variant="primary" onClick={handleFinish}>
                {t("close")}
              </Button>
            </div>
          </div>
        )}
      </Modal.Body>
    </Modal>
  );
};

export default TwoFASetupModal;
